import { useEffect, useState, Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api";
import { fmtDate, fmtDateTime } from "../../lib/format";
import { HEARING_QUESTIONS } from "../../../shared/hearing-questions";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  ErrorBanner,
  SuccessBanner,
  Banner,
} from "../../components/ui";

const MEMBER_STATUS_LABELS: Record<string, string> = {
  active: "有効",
  payment_failed: "決済失敗",
  suspended: "停止",
  cancelled: "解約",
  pending_activation: "仮登録",
};
const MEMBER_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  active: "success",
  payment_failed: "danger",
  suspended: "warning",
  cancelled: "neutral",
  pending_activation: "warning",
};

const APPLICATION_STATUS_LABELS: Record<string, string> = {
  pending_payment: "決済待ち",
  applied: "申込中",
  cancelled: "キャンセル",
  checked_in: "受付済み",
  no_show: "不参加",
};
const APPLICATION_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  pending_payment: "warning",
  applied: "neutral",
  cancelled: "neutral",
  checked_in: "success",
  no_show: "danger",
};

interface MemberDetail {
  id: string;
  member_no?: string;
  memberNo?: string;
  display_name?: string;
  displayName?: string;
  email: string;
  status: string;
  joined_at?: string | null;
  joinedAt?: string | null;
  cancelled_at?: string | null;
  cancelledAt?: string | null;
  phone?: string | null;
  address?: string | null;
  company_name?: string | null;
  companyName?: string | null;
  kana?: string | null;
  plan?: string | null;
  amount?: number | null;
  myasp_user_id?: string | null;
  myasp_status?: number | null;
  isStaff?: boolean;
  subscription?: {
    status: string;
    plan_code?: string | null;
    current_period_start?: string | null;
    current_period_end?: string | null;
  } | null;
}

interface HearingItem {
  code: string;
  label: string;
  type: string;
  valueText: string | null;
  valueJson: string | null;
  sortOrder: number;
}

interface HearingResp {
  answerSet: {
    id: string;
    version: number;
    isCompleted: boolean;
    completedAt: string | null;
    updatedAt: string;
  } | null;
  items: HearingItem[];
}

interface AppRow {
  id: string;
  eventId: string;
  applicationStatus: string;
  participationMode: string;
  paymentStatus: string;
  appliedAt: string;
  cancelledAt: string | null;
  checkedInAt: string | null;
  eventTitle: string;
  eventStartAt: string | null;
}

function pickStr(a: string | null | undefined, b: string | null | undefined): string {
  return (a ?? b ?? "") + "";
}

const hearingOptionMap = new Map<string, Map<string, string>>();
for (const q of HEARING_QUESTIONS) {
  if (q.options) {
    const m = new Map<string, string>();
    for (const o of q.options) m.set(o.value, o.label);
    hearingOptionMap.set(q.code, m);
  }
}

function formatHearingValue(it: HearingItem): string {
  const q = HEARING_QUESTIONS.find((q) => q.code === it.code);
  if (q?.type === "boolean") {
    return it.valueText === "true" ? "はい" : it.valueText === "false" ? "いいえ" : it.valueText ?? "-";
  }
  if (q?.type === "multi_select" && it.valueJson) {
    try {
      const arr: string[] = JSON.parse(it.valueJson);
      const labels = arr.map((v) => hearingOptionMap.get(it.code)?.get(v) ?? v);
      return labels.join("、");
    } catch { /* fallthrough */ }
  }
  if (q?.type === "select" && it.valueText) {
    return hearingOptionMap.get(it.code)?.get(it.valueText) ?? it.valueText;
  }
  if (it.valueText && it.valueText !== "") return it.valueText;
  if (it.valueJson) {
    try {
      const arr = JSON.parse(it.valueJson);
      if (Array.isArray(arr)) return arr.join("、");
    } catch { /* fallthrough */ }
    return it.valueJson;
  }
  return "-";
}

export function AdminMemberDetailPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const [member, setMember] = useState<MemberDetail | null>(null);
  const [hearing, setHearing] = useState<HearingResp | null>(null);
  const [apps, setApps] = useState<AppRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [roleBusy, setRoleBusy] = useState(false);
  // 仮パスワード発行
  const [pwConfirm, setPwConfirm] = useState(false);
  const [pwMode, setPwMode] = useState<"auto" | "manual">("auto");
  const [pwInput, setPwInput] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [issuedPw, setIssuedPw] = useState<string | null>(null);

  const load = async () => {
    if (!memberId) return;
    setErr(null);
    try {
      const [m, h, a] = await Promise.all([
        api.get<MemberDetail>(`/admin/members/${memberId}`),
        api.get<HearingResp>(`/admin/members/${memberId}/hearing`),
        api.get<{ items: AppRow[] }>(`/admin/members/${memberId}/applications`),
      ]);
      setMember(m);
      setHearing(h);
      setApps(a.items);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  useEffect(() => {
    load();
  }, [memberId]);

  const requestStatus = (st: string) => {
    setErr(null);
    setMsg(null);
    setPendingStatus(st);
  };
  const cancelStatus = () => setPendingStatus(null);
  const toggleStaff = async (staff: boolean) => {
    if (!memberId) return;
    setErr(null);
    setMsg(null);
    setRoleBusy(true);
    try {
      await api.patch(`/admin/members/${memberId}/role`, { staff });
      setMsg(
        staff
          ? "スタッフに設定しました（会員数から除外されます）"
          : "会員に戻しました（会員数に含まれます）",
      );
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setRoleBusy(false);
    }
  };
  const confirmStatus = async () => {
    if (!memberId || !pendingStatus) return;
    setErr(null);
    const label = MEMBER_STATUS_LABELS[pendingStatus] ?? pendingStatus;
    try {
      await api.patch(`/admin/members/${memberId}/status`, {
        status: pendingStatus,
      });
      setMsg(`ステータスを「${label}」に変更しました`);
      setPendingStatus(null);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  // 仮パスワード発行。発行後の平文はレスポンスにしか無いので画面に保持する。
  const issuePassword = async () => {
    if (!memberId) return;
    setErr(null);
    setMsg(null);
    setPwBusy(true);
    try {
      const body =
        pwMode === "manual" ? { password: pwInput } : ({} as Record<string, string>);
      const r = await api.post<{ password: string; generated: boolean }>(
        `/admin/members/${memberId}/password`,
        body,
      );
      setIssuedPw(r.password);
      setPwConfirm(false);
      setPwInput("");
      setMsg(
        "パスワードを再設定しました。この会員の既存ログインはすべて無効になりました。",
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setPwBusy(false);
    }
  };

  if (err && !member)
    return (
      <div className="space-y-3">
        <Link to="/admin/members" className="text-sm text-tora underline">
          ← 会員管理へ戻る
        </Link>
        <ErrorBanner>{err}</ErrorBanner>
      </div>
    );
  if (!member) return <Loading />;

  const memberNo = pickStr(member.memberNo, member.member_no);
  const displayName = pickStr(member.displayName, member.display_name);
  const joinedAt = pickStr(member.joinedAt, member.joined_at);
  const cancelledAt = pickStr(member.cancelledAt, member.cancelled_at);
  const companyName = pickStr(member.companyName, member.company_name);

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Link to="/admin/members" className="text-sm text-tora underline">
          ← 会員管理へ戻る
        </Link>
      </div>

      <section className="card p-5 md:p-6">
        <SectionHeading level={2}>会員詳細</SectionHeading>
        {err && <ErrorBanner>{err}</ErrorBanner>}
        {msg && <SuccessBanner>{msg}</SuccessBanner>}
        <dl className="mt-3 grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
          <dt className="text-tora-ink/60">会員番号</dt>
          <dd className="font-mono">{memberNo || "-"}</dd>
          <dt className="text-tora-ink/60">表示名</dt>
          <dd>{displayName || "-"}</dd>
          {member.kana && (
            <>
              <dt className="text-tora-ink/60">フリガナ</dt>
              <dd>{member.kana}</dd>
            </>
          )}
          <dt className="text-tora-ink/60">メール</dt>
          <dd>{member.email}</dd>
          <dt className="text-tora-ink/60">会社名</dt>
          <dd>{companyName || "-"}</dd>
          <dt className="text-tora-ink/60">電話</dt>
          <dd>{member.phone || "-"}</dd>
          <dt className="text-tora-ink/60">住所</dt>
          <dd>{member.address || "-"}</dd>
          <dt className="text-tora-ink/60">サブスク</dt>
          <dd>
            {member.subscription ? (
              <StatusBadge tone={member.subscription.status === "active" ? "success" : member.subscription.status === "cancelled" ? "neutral" : "warning"}>
                {member.subscription.status === "active" ? "有効" : member.subscription.status === "cancelled" ? "解約" : member.subscription.status}
              </StatusBadge>
            ) : (
              <span className="text-tora-ink/40">未契約</span>
            )}
            {member.subscription?.current_period_end && (
              <span className="ml-2 text-xs text-tora-ink/50">
                (次回更新: {fmtDate(member.subscription.current_period_end)})
              </span>
            )}
          </dd>
          {member.plan && (
            <>
              <dt className="text-tora-ink/60">プラン</dt>
              <dd>{member.plan}{member.amount ? ` / ¥${member.amount.toLocaleString()}` : ""}</dd>
            </>
          )}
          <dt className="text-tora-ink/60">ステータス</dt>
          <dd>
            <StatusBadge
              tone={MEMBER_STATUS_TONE[member.status] ?? "neutral"}
            >
              {MEMBER_STATUS_LABELS[member.status] ?? member.status}
            </StatusBadge>
          </dd>
          <dt className="text-tora-ink/60">入会日</dt>
          <dd>{joinedAt ? fmtDate(joinedAt) : "-"}</dd>
          <dt className="text-tora-ink/60">解約日</dt>
          <dd>{cancelledAt ? fmtDate(cancelledAt) : "-"}</dd>
          <dt className="text-tora-ink/60">役割</dt>
          <dd>
            {member.isStaff ? (
              <StatusBadge tone="neutral">スタッフ（会員数から除外）</StatusBadge>
            ) : (
              <StatusBadge tone="success">会員</StatusBadge>
            )}
          </dd>
        </dl>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-sm text-tora-ink/60">役割の変更</label>
          {member.isStaff ? (
            <button
              className="btn-outline text-xs"
              onClick={() => toggleStaff(false)}
              disabled={roleBusy}
            >
              {roleBusy ? "処理中…" : "会員に戻す（会員数に含める）"}
            </button>
          ) : (
            <button
              className="btn-outline text-xs"
              onClick={() => toggleStaff(true)}
              disabled={roleBusy}
            >
              {roleBusy ? "処理中…" : "スタッフに設定（会員数から除外）"}
            </button>
          )}
          <span className="text-[11px] text-tora-ink/50">
            スタッフに設定すると会員管理の「会員数」から除外されます（一覧には表示されます）。
          </span>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <label className="text-sm text-tora-ink/60">ステータス変更</label>
          <select
            className="input max-w-[200px]"
            value=""
            onChange={(e) => {
              if (e.target.value) requestStatus(e.target.value);
            }}
          >
            <option value="">変更…</option>
            <option value="active">有効</option>
            <option value="payment_failed">決済失敗</option>
            <option value="suspended">停止</option>
            <option value="cancelled">解約</option>
          </select>
        </div>
        {pendingStatus && (
          <div className="mt-3">
            <Banner tone="warning">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm">
                  ステータスを「
                  {MEMBER_STATUS_LABELS[pendingStatus] ?? pendingStatus}」に
                  変更します。よろしいですか？
                </span>
                <button
                  className="btn-primary text-xs"
                  onClick={confirmStatus}
                >
                  実行
                </button>
                <button className="btn-outline text-xs" onClick={cancelStatus}>
                  キャンセル
                </button>
              </div>
            </Banner>
          </div>
        )}

        {/* 仮パスワード発行 */}
        <div className="mt-5 pt-4 border-t border-black/10">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">パスワード</span>
            {!pwConfirm && (
              <button
                className="btn-outline text-xs"
                onClick={() => {
                  setErr(null);
                  setMsg(null);
                  setIssuedPw(null);
                  setPwConfirm(true);
                }}
              >
                仮パスワードを発行
              </button>
            )}
          </div>
          <p className="text-[11px] text-tora-ink/50 mt-1">
            会員が再設定メールを受け取れない場合の救済手段です。
            発行するとこの会員の既存ログインはすべて無効になります。
          </p>

          {pwConfirm && (
            <div className="mt-3">
              <Banner tone="warning">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        checked={pwMode === "auto"}
                        onChange={() => setPwMode("auto")}
                      />
                      自動生成 (12 文字)
                    </label>
                    <label className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        checked={pwMode === "manual"}
                        onChange={() => setPwMode("manual")}
                      />
                      自分で決める
                    </label>
                  </div>
                  {pwMode === "manual" && (
                    <input
                      className="input max-w-xs"
                      type="text"
                      value={pwInput}
                      onChange={(e) => setPwInput(e.target.value)}
                      placeholder="8 文字以上"
                      autoFocus
                    />
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm">
                      この会員のパスワードを再設定します。よろしいですか？
                    </span>
                    <button
                      className="btn-primary text-xs"
                      onClick={issuePassword}
                      disabled={
                        pwBusy || (pwMode === "manual" && pwInput.length < 8)
                      }
                    >
                      {pwBusy ? "発行中…" : "実行"}
                    </button>
                    <button
                      className="btn-outline text-xs"
                      onClick={() => {
                        setPwConfirm(false);
                        setPwInput("");
                      }}
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              </Banner>
            </div>
          )}

          {issuedPw && (
            <div className="mt-3">
              <Banner tone="success">
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    発行したパスワード（この画面を離れると再表示できません）
                  </p>
                  <p className="font-mono text-base break-all select-all">
                    {issuedPw}
                  </p>
                  <p className="text-[11px] text-tora-ink/60">
                    会員ご本人へお伝えください。ログイン後にご本人がパスワードを
                    変更できます。
                  </p>
                </div>
              </Banner>
            </div>
          )}
        </div>
      </section>

      <section className="card p-5 md:p-6">
        <SectionHeading level={3}>初回ヒアリング回答</SectionHeading>
        {!hearing?.answerSet ? (
          <p className="text-sm text-tora-ink/60">未入力</p>
        ) : (
          <>
            <p className="text-xs text-tora-ink/60 mb-2">
              {hearing.answerSet.isCompleted ? "完了" : "未完了"}
              {hearing.answerSet.completedAt &&
                `（${fmtDateTime(hearing.answerSet.completedAt)}）`}
            </p>
            {hearing.items.length === 0 ? (
              <p className="text-sm text-tora-ink/60">回答なし</p>
            ) : (
              <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
                {hearing.items.map((it) => (
                  <Fragment key={it.code}>
                    <dt className="text-tora-ink/60">{it.label}</dt>
                    <dd className="whitespace-pre-wrap">
                      {formatHearingValue(it)}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            )}
          </>
        )}
      </section>

      <section className="card p-5 md:p-6">
        <SectionHeading level={3}>申込履歴</SectionHeading>
        {apps.length === 0 ? (
          <p className="text-sm text-tora-ink/60">申込はありません</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-xs border border-black/10">
            <thead className="bg-black/5 text-left">
              <tr>
                <th className="p-2">イベント</th>
                <th className="p-2">開始</th>
                <th className="p-2">ステータス</th>
                <th className="p-2">申込日</th>
                <th className="p-2">受付日時</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-t border-black/10">
                  <td className="p-2">{a.eventTitle}</td>
                  <td className="p-2">{fmtDateTime(a.eventStartAt)}</td>
                  <td className="p-2">
                    <StatusBadge
                      tone={
                        APPLICATION_STATUS_TONE[a.applicationStatus] ??
                        "neutral"
                      }
                      bold={a.applicationStatus === "checked_in"}
                    >
                      {APPLICATION_STATUS_LABELS[a.applicationStatus] ??
                        a.applicationStatus}
                    </StatusBadge>
                  </td>
                  <td className="p-2">{fmtDate(a.appliedAt)}</td>
                  <td className="p-2">{fmtDateTime(a.checkedInAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  );
}
